import type {
  DashboardCockpitReverseIvrAudioSeries,
  DashboardCockpitReverseIvrDigitPoint,
  DashboardCockpitReverseIvrPerformanceEvolutionSlice,
  DashboardCockpitReverseIvrPerformancePoint,
  DashboardCockpitTodayReverseIvrSlice,
  DashboardCockpitTodayReverseIvrSummary,
} from '../types';

export type TodayReverseIvrComparisonKey =
  | 'attemptedCalls'
  | 'answeredCalls'
  | 'heardToEndCalls'
  | 'dtmfCalls'
  | 'mappedCalls'
  | 'timeoutCalls'
  | 'answerRate'
  | 'heardToEndRate'
  | 'dtmfRate'
  | 'mappedRate'
  | 'timeoutRate';

export type TodayReverseIvrComparisonTrend = 'above' | 'below' | 'steady';

export interface TodayReverseIvrComparisonRow {
  key: TodayReverseIvrComparisonKey;
  currentValue: number;
  averageValue: number;
  deltaValue: number;
  deltaPercent: number | null;
  trend: TodayReverseIvrComparisonTrend;
  isRate: boolean;
  lowerIsBetter: boolean;
}

export interface TodayReverseIvrFunnelRow {
  key: 'answeredCalls' | 'heardToEndCalls' | 'dtmfCalls' | 'mappedCalls';
  labelKey: string;
  value: number;
}

const COUNT_STEADY_THRESHOLD_PERCENT = 10;
const RATE_STEADY_THRESHOLD_POINTS = 2;

function roundMetric(value: number, decimals = 2) {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function resolveTrend(deltaValue: number, options: { isRate: boolean }) {
  const threshold = options.isRate ? RATE_STEADY_THRESHOLD_POINTS : COUNT_STEADY_THRESHOLD_PERCENT;
  if (Math.abs(deltaValue) < threshold) {
    return 'steady';
  }
  return deltaValue > 0 ? 'above' : 'below';
}

function buildCountComparisonRow(
  key: Extract<
    TodayReverseIvrComparisonKey,
    'attemptedCalls' | 'answeredCalls' | 'heardToEndCalls' | 'dtmfCalls' | 'mappedCalls' | 'timeoutCalls'
  >,
  currentValue: number,
  averageValue: number,
  lowerIsBetter = false,
): TodayReverseIvrComparisonRow | null {
  if (averageValue <= 0) {
    return null;
  }
  const deltaPercent = ((currentValue - averageValue) / averageValue) * 100;
  return {
    key,
    currentValue,
    averageValue: roundMetric(averageValue),
    deltaValue: roundMetric(deltaPercent),
    deltaPercent: roundMetric(deltaPercent),
    trend: resolveTrend(deltaPercent, { isRate: false }),
    isRate: false,
    lowerIsBetter,
  };
}

function buildRateComparisonRow(
  key: Extract<
    TodayReverseIvrComparisonKey,
    'answerRate' | 'heardToEndRate' | 'dtmfRate' | 'mappedRate' | 'timeoutRate'
  >,
  currentValue: number,
  averageValue: number,
  lowerIsBetter = false,
): TodayReverseIvrComparisonRow {
  const deltaPoints = currentValue - averageValue;
  return {
    key,
    currentValue,
    averageValue: roundMetric(averageValue),
    deltaValue: roundMetric(deltaPoints),
    deltaPercent: null,
    trend: resolveTrend(deltaPoints, { isRate: true }),
    isRate: true,
    lowerIsBetter,
  };
}

/**
 * Build comparable rows from the backend's same-elapsed-day Reverse IVR baseline.
 *
 * The baseline already matches today's local-day elapsed window, so the
 * frontend only calculates presentation deltas and trend labels.
 */
export function buildTodayReverseIvrRecentComparison(
  todayReverseIvr: DashboardCockpitTodayReverseIvrSlice | null,
): TodayReverseIvrComparisonRow[] {
  const comparison = todayReverseIvr?.recentComparison;
  if (!todayReverseIvr || !comparison?.hasSufficientSample) {
    return [];
  }

  return [
    buildCountComparisonRow(
      'attemptedCalls',
      todayReverseIvr.attemptedCalls,
      comparison.attemptedCallsAverage,
    ),
    buildCountComparisonRow(
      'answeredCalls',
      todayReverseIvr.answeredCalls,
      comparison.answeredCallsAverage,
    ),
    buildCountComparisonRow(
      'heardToEndCalls',
      todayReverseIvr.heardToEndCalls,
      comparison.heardToEndCallsAverage,
    ),
    buildCountComparisonRow(
      'dtmfCalls',
      todayReverseIvr.dtmfCalls,
      comparison.dtmfCallsAverage,
    ),
    buildCountComparisonRow(
      'mappedCalls',
      todayReverseIvr.mappedCalls,
      comparison.mappedCallsAverage,
    ),
    buildCountComparisonRow(
      'timeoutCalls',
      todayReverseIvr.timeoutCalls,
      comparison.timeoutCallsAverage,
      true,
    ),
    buildRateComparisonRow(
      'answerRate',
      todayReverseIvr.attemptedCalls > 0
        ? todayReverseIvr.answeredCalls / todayReverseIvr.attemptedCalls * 100
        : 0,
      comparison.answerRate,
    ),
    buildRateComparisonRow(
      'heardToEndRate',
      todayReverseIvr.heardToEndRate,
      comparison.heardToEndRate,
    ),
    buildRateComparisonRow(
      'dtmfRate',
      todayReverseIvr.dtmfRate,
      comparison.dtmfRate,
    ),
    buildRateComparisonRow(
      'mappedRate',
      todayReverseIvr.mappedRate,
      comparison.mappedRate,
    ),
    buildRateComparisonRow(
      'timeoutRate',
      todayReverseIvr.timeoutRate,
      comparison.timeoutRate,
      true,
    ),
  ].filter((row): row is TodayReverseIvrComparisonRow => Boolean(row));
}

/**
 * Build the compact Reverse IVR funnel without the raw attempted-call bar.
 *
 * Calls placed are intentionally shown as a numeric card because that volume
 * can dwarf DTMF and mapped outcomes, making the useful steps unreadable.
 */
export function buildTodayReverseIvrFunnel(
  todayReverseIvr: DashboardCockpitTodayReverseIvrSummary,
): TodayReverseIvrFunnelRow[] {
  return [
    {
      key: 'answeredCalls',
      labelKey: 'pages:dashboard.todayMetrics.answeredCalls',
      value: todayReverseIvr.answeredCalls,
    },
    {
      key: 'heardToEndCalls',
      labelKey: 'pages:dashboard.todayMetrics.heardToEndCalls',
      value: todayReverseIvr.heardToEndCalls,
    },
    {
      key: 'dtmfCalls',
      labelKey: 'pages:dashboard.todayMetrics.dtmfCalls',
      value: todayReverseIvr.dtmfCalls,
    },
    {
      key: 'mappedCalls',
      labelKey: 'pages:dashboard.todayMetrics.mappedCalls',
      value: todayReverseIvr.mappedCalls,
    },
  ];
}

export function getReverseIvrScopePerformance(
  performanceEvolution: DashboardCockpitReverseIvrPerformanceEvolutionSlice | null | undefined,
  audioSeries: DashboardCockpitReverseIvrAudioSeries | null | undefined,
): DashboardCockpitReverseIvrPerformancePoint[] {
  return audioSeries?.points ?? performanceEvolution?.points ?? [];
}

export function getReverseIvrScopeHeatmap(
  globalHeatmap: DashboardCockpitReverseIvrAudioSeries['heatmap'] | null | undefined,
  audioSeries: DashboardCockpitReverseIvrAudioSeries | null | undefined,
) {
  return audioSeries?.heatmap ?? globalHeatmap ?? [];
}

export function getReverseIvrScopeDigitDistribution(
  globalDigitDistribution: DashboardCockpitReverseIvrDigitPoint[] | null | undefined,
  audioSeries: DashboardCockpitReverseIvrAudioSeries | null | undefined,
) {
  return audioSeries?.digitDistribution ?? globalDigitDistribution ?? [];
}
